import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, ArrowRight, Sparkles, Info, Building2, Banknote, Layers } from "lucide-react";
import ReserveCampaignModal from "@/components/marketing/ReserveCampaignModal";
import LiveReaders from "@/components/marketing/LiveReaders";
import CodeRain from "@/components/marketing/CodeRain";
import { useCodeRain } from "@/lib/siteEffects";

const TIERS = {
  starter: { slug: "starter", name: "Starter", monthly_gbp: 99,  leads_cap: 500,    desc: "For lean teams launching their first campaigns.",   pts: ["Up to 500 leads / month", "Direct-post & webhooks", "AI lead qualification", "Email + chat support"] },
  growth:  { slug: "growth",  name: "Growth",  monthly_gbp: 149, leads_cap: 2000,   desc: "For scaling operators routing multiple sectors.",   pts: ["Up to 2,000 leads / month", "Ping trees + AI systems", "Outbound voice agent", "Dedicated success manager"], badge: "Most popular" },
  scale:   { slug: "scale",   name: "Scale",   monthly_gbp: 249, leads_cap: 10000,  desc: "For volume buyers and lead-exchange operators.",    pts: ["Up to 10,000 leads / month", "White-label portals", "SSO + audit log", "Volume routing rebates"] },
};

export default function Pricing() {
  const rain = useCodeRain("pricing");
  const [sectors, setSectors] = useState([]);
  const [types, setTypes] = useState([]);
  const [delivery, setDelivery] = useState({ methods: [], exclusivity: [] });
  const [order, setOrder] = useState({
    sector: "mortgages",
    acquisition_type: "exclusive",
    volume: 500,
    delivery_method: "Direct Post API",
    exclusivity: "Standard",
    tier: "starter",
  });
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);

  useEffect(() => {
    api.get("/catalog/sectors").then(r => setSectors(r.data));
    api.get("/catalog/acquisition-types").then(r => setTypes(r.data));
    api.get("/catalog/delivery-methods").then(r => setDelivery(r.data));
  }, []);

  // Live re-price whenever the configurator state changes
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.post("/pricing/estimate", order).then(r => { if (!cancel) setEstimate(r.data); }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [order]);

  const sectorLabel = sectors.find(s => s.slug === order.sector)?.name || "Select sector";
  const typeLabel = types.find(t => t.slug === order.acquisition_type)?.name || "Select acquisition type";
  const exclusivityLabel = (delivery.exclusivity || []).find(e => e === order.exclusivity) || order.exclusivity;
  const deliveryLabel = (delivery.methods || []).find(m => m === order.delivery_method) || order.delivery_method;

  const tierMeta = TIERS[order.tier];
  const tierMonthly = tierMeta?.monthly_gbp || 0;
  const leadsMonthly = estimate?.total || 0;
  const grandTotal = tierMonthly + leadsMonthly;

  return (
    <div>
      {/* ===== HERO ===== */}
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">Pricing</p>
          <h1 className="mt-3 text-5xl sm:text-6xl tracking-[-0.04em] font-semibold text-slate-900 max-w-3xl">Acquisition infrastructure, priced like infrastructure.</h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-600">Configure your sector, exclusivity, filters and delivery — see your per-lead and monthly economics before you commit.</p>
          <div className="mt-6">
            <Link to="/why-connexis" className="inline-flex items-center gap-1.5 text-[13px] text-[#0055FF] hover:text-[#0044CC] font-medium" data-testid="pricing-compare-link">
              See how Connexis compares to LeadProsper, LeadByte and boberdoo →
            </Link>
          </div>
        </div>
      </section>

      {/* ===== HOW PRICING WORKS — explainer ===== */}
      <section className="py-12 border-b border-slate-200 bg-slate-50/50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-[#0055FF] mb-4">
            <Info className="size-4" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em]">How Connexis pricing works</span>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <ExplainerCard
              Icon={Layers}
              title="Platform plan"
              subtitle="30-day prepaid renewal"
              body="One simple monthly fee for access to the Connexis operating system — API, dashboards, ping-tree engine, AI systems, compliance toolchain and human support. Plan renews every 30 days; you'll get a renewal link 5 days before expiry. Lead spend is separate, pay-as-you-go."
              accent="From £99/mo"
            />
            <ExplainerCard
              Icon={Banknote}
              title="Per-lead price"
              subtitle="Prepaid wallet · one price per sector"
              body="Top up your wallet (£5,000 / £15,000 / £30,000) via card or bank transfer. Every accepted lead is debited at one flat sector price — no hidden multipliers, no extra margin row. Stop / pause anytime, your balance never expires."
              accent="From £14/lead"
            />
            <ExplainerCard
              Icon={Building2}
              title="No surprises"
              subtitle="Reservation · 24-hour compliance review"
              body="Reserve your campaign, our compliance team reviews within 24h, then fund your wallet and leads start flowing immediately. The first top-up is £5,000 minimum, and the wallet auto-pauses at £0 so you'll never overspend."
              accent="£5,000 minimum top-up"
            />
          </div>
          <div className="mt-6 text-center text-[12px] text-slate-500">
            Volume above 10,000 leads/mo is custom-quoted — <Link to="/book-demo" className="text-[#0055FF] hover:underline">talk to our team</Link> for negotiated rates.
          </div>
        </div>
      </section>

      {/* ===== CONFIGURATOR ===== */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="pricing-configurator">
            <h2 className="text-xl font-medium text-slate-900 tracking-tight">Order configurator</h2>
            <p className="mt-1.5 text-[13px] text-slate-500">Live estimate. Reserve in minutes — we review compliance within 24h, then you top up your wallet to start receiving leads.</p>

            {/* TIER PICKER */}
            <div className="mt-7">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Platform tier</div>
              <div className="grid sm:grid-cols-3 gap-3">
                {Object.values(TIERS).map((t) => {
                  const active = order.tier === t.slug;
                  return (
                    <button
                      key={t.slug}
                      onClick={() => setOrder({ ...order, tier: t.slug })}
                      className={`text-left rounded-xl border p-4 transition-all ${active ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]/15" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      data-testid={`cfg-tier-${t.slug}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900 text-sm">{t.name}</span>
                        {t.badge && <span className="text-[9px] font-semibold uppercase tracking-wider bg-[#0055FF]/10 text-[#0055FF] px-1.5 py-0.5 rounded-full">{t.badge}</span>}
                      </div>
                      <div className="mt-2 text-[13px] font-mono tabular-nums text-slate-900">
                        £{t.monthly_gbp.toLocaleString()}/mo
                      </div>
                      <div className="mt-1 text-[11.5px] text-slate-500 leading-snug">{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* CAMPAIGN FIELDS */}
            <div className="mt-7">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Campaign</div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Sector">
                  <Select value={order.sector} onValueChange={(v) => setOrder({ ...order, sector: v })}>
                    <SelectTrigger data-testid="cfg-sector"><SelectValue placeholder="Select sector" /></SelectTrigger>
                    <SelectContent>{sectors.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Acquisition type">
                  <Select value={order.acquisition_type} onValueChange={(v) => setOrder({ ...order, acquisition_type: v })}>
                    <SelectTrigger data-testid="cfg-acquisition"><SelectValue placeholder="Select acquisition type" /></SelectTrigger>
                    <SelectContent>{types.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Exclusivity tier">
                  <Select value={order.exclusivity} onValueChange={(v) => setOrder({ ...order, exclusivity: v })}>
                    <SelectTrigger data-testid="cfg-exclusivity"><SelectValue placeholder="Select exclusivity tier" /></SelectTrigger>
                    <SelectContent>{delivery.exclusivity.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Delivery method">
                  <Select value={order.delivery_method} onValueChange={(v) => setOrder({ ...order, delivery_method: v })}>
                    <SelectTrigger data-testid="cfg-delivery"><SelectValue placeholder="Select delivery method" /></SelectTrigger>
                    <SelectContent>{delivery.methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Monthly volume">
                  <Input type="number" min={50} step={50} value={order.volume} onChange={(e) => setOrder({ ...order, volume: parseInt(e.target.value) || 0 })} data-testid="cfg-volume" />
                </Field>
                <Field label="Currency"><Input value="GBP" disabled /></Field>
              </div>
            </div>

            {/* ACQUISITION TYPE QUICK-PICK */}
            <div className="mt-8">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Acquisition types</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {types.map((t) => {
                  const active = t.slug === order.acquisition_type;
                  return (
                    <button key={t.slug} onClick={() => setOrder({ ...order, acquisition_type: t.slug })}
                      className={`text-left rounded-xl border p-4 transition-all ${active ? "border-[#0055FF] bg-[#0055FF]/[0.04]" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`cfg-type-${t.slug}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-slate-900">{t.name}</div>
                        {active && <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#0055FF]/10 text-[#0055FF] px-1.5 py-0.5 rounded-full">Selected</span>}
                      </div>
                      <div className="mt-1 text-xs text-slate-600">{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ===== LIVE ESTIMATE — sticky summary ===== */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-900 text-white p-6 sm:p-8 sticky top-24" data-testid="pricing-summary">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Live estimate</span>
                <span className="inline-flex items-center gap-1.5 text-xs text-emerald-300"><span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />{loading ? "Calculating…" : "Synced"}</span>
              </div>

              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-widest text-slate-400">Estimated unit price</div>
                <div className="mt-1 text-5xl tracking-[-0.04em] font-semibold tabular-nums">£{estimate?.unit_price?.toFixed(2) || "—"}</div>
              </div>

              <div className="mt-7 space-y-3 text-sm">
                <Row k="Volume / mo" v={`${order.volume.toLocaleString()} leads`} />
                <Row k="Lead spend (max)" v={estimate ? `£${estimate.total.toLocaleString()}/mo` : "—"} />
                <div className="border-t border-white/10 pt-3">
                  <Row k={`${tierMeta?.name || "—"} plan`} v={tierMonthly ? `£${tierMonthly.toLocaleString()}/mo` : "Custom"} />
                </div>
                <div className="border-t border-white/10 pt-3 flex items-center justify-between font-medium">
                  <span>Max monthly budget</span>
                  <span className="text-2xl tabular-nums">{estimate?.custom_quote ? "Custom" : `£${grandTotal.toLocaleString()}`}</span>
                </div>
                <div className="text-[11px] text-slate-400 leading-snug">
                  You only pay for <span className="text-slate-200 font-medium">accepted</span> leads. Wallet auto-pauses delivery if balance hits £0.
                </div>
                {estimate?.custom_quote && (
                  <div className="text-[11px] text-amber-300 mt-1">
                    Volume above 10,000 leads/mo · custom-quoted by our team.
                  </div>
                )}
              </div>

              {/* Live social-proof — sits between total + CTA for max conversion lift */}
              <div className="mt-5 -mb-2 flex justify-between items-center">
                <LiveReaders page="pricing-sidebar" variant="inline" tone="dark" />
              </div>

              {estimate?.custom_quote || tierMeta?.monthly_gbp == null ? (
                <Link to="/book-demo">
                  <Button className="w-full mt-7 rounded-full h-11 bg-white text-slate-900 hover:bg-slate-100" data-testid="pricing-talk-sales-btn">
                    Talk to sales for a custom quote <ArrowRight className="size-4" />
                  </Button>
                </Link>
              ) : (
                <Button onClick={() => setReserveOpen(true)} className="w-full mt-7 rounded-full h-11 bg-white text-slate-900 hover:bg-slate-100" data-testid="pricing-reserve-btn">
                  Reserve campaign <ArrowRight className="size-4" />
                </Button>
              )}

              <ul className="mt-6 space-y-2 text-xs text-slate-300">
                {["Real-time delivery (≤1s)", "Compliance + consent capture", "Dedicated account manager", "API + dashboard + webhooks"].map((b, i) =>
                  <li key={i} className="flex items-start gap-2"><CheckCircle2 className="size-3.5 text-emerald-400 mt-0.5" /> {b}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PLATFORM TIERS — detail cards ===== */}
      <section className="relative overflow-hidden py-20 bg-slate-50/60 border-y border-slate-200">
        {rain.on && <CodeRain variant="light" intensity={rain.intensity} />}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-primary"><Sparkles className="size-4" /><span className="text-xs font-semibold uppercase tracking-widest">Platform tiers</span></div>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-[-0.02em] font-medium text-slate-900">Choose the operational tier that fits.</h2>
          <p className="mt-3 text-slate-600 max-w-2xl text-[15px]">The platform fee covers infrastructure access — API, dashboards, ping trees, AI systems and support. Per-lead spend is separate, paid only on leads we route into your account.</p>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {Object.values(TIERS).map((t, i) => (
              <div key={i} className={`relative rounded-2xl border p-7 ${t.badge ? "border-[#0055FF] bg-white shadow-float" : "border-slate-200 bg-white"}`} data-testid={`tier-card-${i}`}>
                {t.badge && <span className="absolute top-4 right-4 rounded-full bg-[#0055FF]/10 text-[#0055FF] text-[10px] font-semibold px-2 py-1 tracking-wider uppercase">{t.badge}</span>}
                <div className="text-sm font-medium text-slate-700">{t.name}</div>
                <div className="mt-4 flex items-baseline gap-1.5">
                  <span className="text-4xl tracking-tight font-semibold text-slate-900">£{t.monthly_gbp.toLocaleString()}</span>
                  <span className="text-sm text-slate-500">/mo plan</span>
                </div>
                <p className="mt-2 text-sm text-slate-600">{t.desc}</p>
                <ul className="mt-5 space-y-2">{t.pts.map((p, k) => <li key={k} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 className="size-4 text-[#0055FF]" /> {p}</li>)}</ul>

                <Button onClick={() => { setOrder({ ...order, tier: t.slug }); setTimeout(() => setReserveOpen(true), 80); }} className="mt-6 w-full rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid={`tier-${t.slug}-reserve`}>
                  Reserve campaign
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">Routing more than 10,000 leads/month? <Link to="/book-demo" className="text-[#0055FF] hover:underline font-medium">Talk to sales for a custom enterprise quote.</Link></p>
          </div>
        </div>
      </section>

      <ReserveCampaignModal
        open={reserveOpen}
        onOpenChange={setReserveOpen}
        order={order}
        estimate={estimate}
        tierMeta={tierMeta}
      />

      {/* ===== HOW WE MAKE MONEY — transparency block ===== */}
      <section className="py-16 border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-[11px] uppercase tracking-widest text-slate-600">
            <Sparkles className="size-3 text-[#0055FF]" /> How we make money
          </div>
          <h3 className="mt-5 text-2xl sm:text-3xl tracking-[-0.02em] font-medium text-slate-900">
            Transparent margin. No hidden uplift.
          </h3>
          <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">
            On every lead you buy, we keep a flat <span className="font-semibold text-slate-900">10 % routing margin</span> — paid by the lead seller (publisher), not added to your price. If a mortgage lead costs you <span className="font-mono">£38</span>, the publisher receives <span className="font-mono">£34.20</span> and Connexis keeps <span className="font-mono">£3.80</span>. That's our entire revenue model on the lead side — no markups, no opaque routing fees, no surprise invoices.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto" data-testid="how-we-make-money-split">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">You pay</div>
              <div className="mt-1 text-2xl font-semibold text-slate-900 tabular-nums">£38.00</div>
              <div className="mt-1 text-[11.5px] text-slate-500">flat sector price</div>
            </div>
            <div className="rounded-xl border border-[#0055FF]/20 bg-[#0055FF]/[0.04] p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#0055FF]">Publisher earns</div>
              <div className="mt-1 text-2xl font-semibold text-[#0055FF] tabular-nums">£34.20</div>
              <div className="mt-1 text-[11.5px] text-slate-600">90 % to the source</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Connexis keeps</div>
              <div className="mt-1 text-2xl font-semibold text-slate-900 tabular-nums">£3.80</div>
              <div className="mt-1 text-[11.5px] text-slate-500">10 % routing margin</div>
            </div>
          </div>
        </div>
      </section>

      <LiveReaders page="pricing" />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-slate-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function Row({ k, v, subtle }) {
  return (
    <div className="flex items-center justify-between">
      <span className={subtle ? "text-slate-500 text-[12.5px]" : "text-slate-400"}>{k}</span>
      <span className={`font-mono tabular-nums ${subtle ? "text-slate-300 text-[12.5px]" : ""}`}>{v}</span>
    </div>
  );
}

function ExplainerCard({ Icon, title, subtitle, body, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0F172A] text-white">
        <Icon className="size-5" strokeWidth={1.6} />
      </span>
      <div className="mt-4">
        <div className="text-[15px] font-semibold text-slate-900 tracking-tight">{title}</div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mt-1">{subtitle}</div>
      </div>
      <p className="mt-3 text-[13px] text-slate-600 leading-relaxed">{body}</p>
      <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-mono tabular-nums text-[#0055FF] font-semibold">
        {accent}
      </div>
    </div>
  );
}
